// Device-class helpers. Two distinct concerns share the same three labels:
//
//   - detectDeviceClass() is viewport-only and drives LAYOUT (the
//     data-device-class attribute, responsive CSS, ViewportGate). Shrinking a
//     desktop window to phone width should switch to the phone layout, so this
//     follows the viewport, not the device.
//   - authDeviceClass() mirrors the server's authoritative classifier
//     (internal/deviceclass, ) and drives AUTH: the viewportWidth hint
//     sent at sign-in and the X-Device-Class header sent on refresh. It is
//     User-Agent-primary so it agrees with the class the server bound at
//     sign-in; if it didn't, every refresh on an unchanged device would
//     spuriously trip the server's device-class-switch check and force re-auth.
//
// The pre-paint version of detectDeviceClass lives inline in index.html.

export type DeviceClass = 'phone' | 'tablet' | 'desktop';

const PHONE_MAX = 768;
const TABLET_MAX = 1024;

export function detectDeviceClass(): DeviceClass {
  const w = window.innerWidth;
  if (w <= PHONE_MAX) return 'phone';
  if (w <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

// authDeviceClass mirrors the Go classifier deviceclass.Classify exactly:
// User-Agent decides phone/tablet/desktop; the viewport hint is advisory and
// only narrows desktop→tablet for an iPadOS Safari reporting a macOS UA. Keep
// this in lockstep with internal/deviceclass/classify.go — they must produce
// the same class for the same (UA, width) or refresh will force re-auth.
export function authDeviceClass(
  userAgent: string = navigator.userAgent,
  viewportHint: number = window.innerWidth,
): DeviceClass {
  const ua = userAgent.trim().toLowerCase();
  if (ua === '') return 'phone';
  // Headless / bot agents → most restrictive (before the desktop-OS branch).
  if (/headless|phantomjs|bot|crawler|spider|slurp/.test(ua)) return 'phone';
  // Hard phone signals — viewport never consulted, so a spoofed width can't
  // widen a phone.
  if (ua.includes('iphone') || ua.includes('ipod')) return 'phone';
  if (ua.includes('android') && ua.includes('mobile')) return 'phone';
  if (/windows phone|iemobile|blackberry|bb10|opera mini|opera mobi/.test(ua)) return 'phone';
  // Hard tablet signals (iPad before the macOS branch — its UA says "Mac OS X").
  if (ua.includes('ipad')) return 'tablet';
  if (ua.includes('android')) return 'tablet';
  if (/silk|kindle|playbook/.test(ua)) return 'tablet';
  // Desktop OS, with the iPadOS-as-macOS narrowing (desktop→tablet only).
  if (/macintosh|mac os x|windows nt|cros|x11|linux/.test(ua)) {
    const isMac = ua.includes('macintosh') || ua.includes('mac os x');
    if (isMac && viewportHint > 0 && viewportHint <= TABLET_MAX) return 'tablet';
    return 'desktop';
  }
  return 'phone';
}

export function setRootDeviceClass(): DeviceClass {
  const dc = detectDeviceClass();
  document.documentElement.setAttribute('data-device-class', dc);
  return dc;
}

type Listener = (dc: DeviceClass) => void;

const listeners = new Set<Listener>();
let current: DeviceClass = detectDeviceClass();
let resizeTimer: number | null = null;

window.addEventListener('resize', () => {
  if (resizeTimer !== null) window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    const next = detectDeviceClass();
    if (next !== current) {
      current = next;
      document.documentElement.setAttribute('data-device-class', next);
      listeners.forEach((fn) => fn(next));
    }
  }, 150);
});

export function onDeviceClassChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getDeviceClass(): DeviceClass {
  return current;
}
